import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';

// Landing page after clicking an invite email OR a "forgot password"
// reset link. Supabase's client picks up the #access_token from the URL
// fragment automatically and opens a temporary session, so by the time
// this component mounts the user is already signed in — they just need
// to choose a password.
//
// Same page handles both types because the flow is identical from the
// user's perspective; the only visible difference is the intro copy.
type FlowType = 'invite' | 'recovery' | 'unknown';

function detectFlowType(): FlowType {
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  if (hash.includes('type=invite')) return 'invite';
  if (hash.includes('type=recovery')) return 'recovery';
  return 'unknown';
}

export default function SetPasswordPage() {
  const [flow] = useState<FlowType>(() => detectFlowType());
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  // "Show password" toggle. The main reason people hit the
  // "same as current password" error on the recovery flow is a
  // password manager silently autofilling their OLD password on the
  // new-password field. Giving them an eye icon lets them SEE what's
  // in the box before clicking save — usually the source of the
  // "but I typed something different!" frustration.
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setHasSession(Boolean(data.session));
      setCheckingSession(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // U-ADM-10 · pre-launch audit hardening. The client rules must match
  // the backend's Supabase Auth policy exactly, otherwise the user
  // clears the on-screen checklist, submits, and gets a fresh rule
  // they didn't know about. Post batch 3, `supabase/config.toml` sets
  // `minimum_password_length = 12` and
  // `password_requirements = "lower_upper_letters_digits"`, so we
  // mirror the same four rules:
  //   * ≥ 12 chars
  //   * ≥ 1 lowercase Latin letter
  //   * ≥ 1 uppercase Latin letter
  //   * ≥ 1 digit
  // Symbols are NOT required (see the AUTH-01 rationale in config).
  interface Rule { label: string; ok: boolean }
  const passwordRules = (pw: string): Rule[] => ([
    { label: '١٢ حرف على الأقل',           ok: pw.length >= 12 },
    { label: 'حرف صغير (a-z)',              ok: /[a-z]/.test(pw) },
    { label: 'حرف كبير (A-Z)',             ok: /[A-Z]/.test(pw) },
    { label: 'رقم (0-9)',                   ok: /[0-9]/.test(pw) },
  ]);

  const passwordIssues = (pw: string): string[] =>
    passwordRules(pw).filter((r) => !r.ok).map((r) => r.label);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const issues = passwordIssues(password);
    if (issues.length) {
      setError('كلمة السر لازم تحتوي على: ' + issues.join('، '));
      return;
    }
    if (password !== confirm) {
      setError('التأكيد مش مطابق لكلمة السر');
      return;
    }

    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) {
      setBusy(false);
      // Translate common Supabase Auth messages to friendly Arabic —
      // most notably the giant charset dump Supabase sends when
      // "Password requirements" is enabled in Auth settings.
      const msg = err.message || '';
      if (/should be different/i.test(msg) || /same as/i.test(msg) || /same_password/i.test(msg)) {
        setError(
          'الباسورد اللي كتبتها دلوقتي طلعت زي القديمة بالظبط. لو مش قصدك ده — على الأغلب الـ password manager بيملّي القديم أوتوماتيك. اضغط زر "إظهار كلمة السر" تحت الحقل عشان تشوف اللي اتكتب فعلاً وامسحه واكتب واحدة جديدة.',
        );
      } else if (/character of each|contain at least one/i.test(msg)) {
        // e.g. "Password should contain at least one character of each: abcdefghijklmnopqrstuvwxyz, ABCDEF..., 0123456789, !@#$%^&*()..."
        setError('كلمة السر ضعيفة — لازم تكون فيها حرف صغير + حرف كبير + رقم + رمز مثل ! @ # $ %.');
      } else if (/weak/i.test(msg) || /leaked/i.test(msg)) {
        setError('كلمة السر دي معروفه أو ضعيفه. جرّب واحده أطول وأصعب.');
      } else if (/rate/i.test(msg)) {
        setError('حاول تاني بعد شويه — عدد محاولات كتير في وقت قصير.');
      } else if (/expired|invalid/i.test(msg)) {
        setError('الرابط انتهت صلاحيته. اطلب رابط جديد من شاشة الدخول.');
      } else {
        setError(msg || 'حصل خطأ. حاول تاني.');
      }
      return;
    }

    // Sign out the recovery session and send them back to the login
    // page so they log in with the NEW password explicitly — not
    // auto-logged-in via the recovery session (which was confusing).
    setDone(true);
    await supabase.auth.signOut();
    setTimeout(() => {
      window.location.href = '/';
    }, 1300);
  };

  const heading =
    flow === 'invite'
      ? 'أهلاً بيك في مرسال — حدد كلمة السر'
      : 'اختار كلمة سر جديدة';

  const lede =
    flow === 'invite'
      ? 'اتدعيت للانضمام لفريق إدارة مرسال. اختار كلمة سر قوية للحساب.'
      : 'اختار كلمة سر جديدة عشان تدخل بها المرة الجاية.';

  if (checkingSession) {
    return <div className="center">جاري التحقق من الرابط...</div>;
  }

  if (!hasSession) {
    return (
      <div className="center login-scene">
        <div className="login-aurora" aria-hidden="true">
          <span className="glow glow-a" />
          <span className="glow glow-b" />
        </div>
        <div className="login-card">
          <div className="login-brand">
            <img
              className="logo"
              src={`${import.meta.env.BASE_URL}brand/mersal-logo.png`}
              alt="Mersal"
            />
          </div>
          <div className="login-heading">
            <h1 className="login-title">الرابط منتهي أو غير صالح</h1>
            <span className="login-underline" aria-hidden="true" />
          </div>
          <p style={{ marginTop: 12, color: 'var(--text-secondary)' }}>
            الرابط ده انتهت صلاحيته أو استُخدم مرة. اطلب من الـ Super Admin
            يبعتلك دعوة جديدة، أو اضغط "نسيت كلمة السر" من شاشة الدخول.
          </p>
          <div className="login-btn-row">
            <a href="/" className="btn login-btn">
              الرجوع لشاشة الدخول
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`center login-scene ${done ? 'login-scene--success' : ''}`}>
      <div className="login-aurora" aria-hidden="true">
        <span className="glow glow-a" />
        <span className="glow glow-b" />
      </div>

      <form
        className={`login-card ${done ? 'login-card--success' : ''}`}
        onSubmit={submit}
      >
        <div className="login-brand">
          <div className="logo-halo" aria-hidden="true" />
          <img
            className="logo"
            src={`${import.meta.env.BASE_URL}brand/mersal-logo.png`}
            alt="Mersal"
          />
        </div>

        <div className="login-heading">
          <h1 className="login-title" data-text={heading}>
            {heading}
          </h1>
          <span className="login-underline" aria-hidden="true" />
          <p className="login-lede" style={{ marginTop: 12 }}>
            {lede}
          </p>
        </div>

        {error && <div className="error-inline">{error}</div>}

        <div className={`field field--interactive ${password ? 'field--filled' : ''}`}>
          <label htmlFor="new-password">كلمة السر الجديدة</label>
          <input
            id="new-password"
            type={reveal ? 'text' : 'password'}
            required
            minLength={12}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/*
            U-ADM-10 · live strength meter matching the backend's four
            rules exactly. Green tick when a rule is met, dim otherwise.
            Shown only while the user is typing.
          */}
          {password.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '8px 0 0',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '4px 12px',
                fontSize: 12,
              }}
              aria-live="polite"
            >
              {passwordRules(password).map((r) => (
                <li
                  key={r.label}
                  style={{
                    color: r.ok ? 'var(--success)' : 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span aria-hidden="true">{r.ok ? '✓' : '○'}</span>
                  {r.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={`field field--interactive ${confirm ? 'field--filled' : ''}`}>
          <label htmlFor="confirm-password">تأكيد كلمة السر</label>
          <input
            id="confirm-password"
            type={reveal ? 'text' : 'password'}
            required
            minLength={12}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--teal)',
              fontFamily: 'inherit',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 6,
            }}
            aria-pressed={reveal}
          >
            {reveal ? 'إخفاء كلمة السر' : 'إظهار كلمة السر'}
          </button>
        </div>

        <div className="login-btn-row">
          <button type="submit" className="btn login-btn" disabled={busy || done}>
            {done ? (
              <span className="login-btn-success" aria-hidden="true">
                <span className="checkmark" />
                <span>تم تغيير كلمة السر — بترجّعك للدخول...</span>
              </span>
            ) : busy ? (
              'جاري الحفظ...'
            ) : (
              'حفظ كلمة السر'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
